import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { PerformanceWidgetSelect } from 'components/atoms/PerformanceWidgetSelect';
import { formatDisplayNumber } from 'helpers/formatHelpers';
import {
  type PerformanceData,
  type PerformanceScopeKey,
} from 'types/performance';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';

type Period = 'today' | 'yesterday';
type ScopeKey = PerformanceScopeKey;

const NAVY = '#181e3a';
const CIRCLE_GRAY = '#e5e7eb';

const SCOPE_OPTIONS: { label: string; value: ScopeKey }[] = [
  { label: 'Me', value: 'me' },
  { label: 'Team', value: 'team' },
  { label: 'Region', value: 'region' },
  { label: 'Coast', value: 'coast' },
  { label: 'All', value: 'all' },
];

type DailySnapshotProps = {
  data: PerformanceData;
  allowedScopes: PerformanceScopeKey[];
  scope: PerformanceScopeKey;
  onScopeChange: (scope: PerformanceScopeKey) => void;
  orgType: NewDashboardOrgType;
};

const DailySnapshot = ({
  data,
  allowedScopes,
  scope,
  onScopeChange,
  orgType,
}: DailySnapshotProps) => {
  const scopeOptions = useMemo(
    () => SCOPE_OPTIONS.filter((o) => allowedScopes.includes(o.value)),
    [allowedScopes],
  );

  const [period, setPeriod] = useState<Period>('today');

  const metrics = useMemo(() => {
    const scopeData = data[scope];
    if (!scopeData) {
      return null;
    }

    const dayRows =
      period === 'today' ? scopeData.current.day : scopeData.last.day;
    const row = dayRows?.[0];
    return row?.metrics ?? null;
  }, [data, period, scope]);

  const pitches = metrics?.Pitches?.value ?? 0;
  const accounts = metrics?.Accounts?.value ?? 0;
  const sits = metrics?.SIT?.value ?? 0;
  const leads = metrics?.Leads?.value ?? 0;
  const hours = metrics?.Hours?.value ?? 0;
  const oppty = metrics?.Oppty?.value ?? 0;
  const sit = metrics?.Sit?.value ?? 0;
  const sales = metrics?.Sales ?? {
    goal: 0,
    pace: 0,
    value: 0,
  };
  const close = metrics?.Close ?? {
    goal: 0,
    pace: 0,
    value: 0,
  };

  return (
    <Root>
      <HeaderRow>
        <Tabs>
          <Tab
            type="button"
            $active={period === 'today'}
            onClick={() => setPeriod('today')}
            aria-pressed={period === 'today'}>
            Today
          </Tab>
          <Tab
            type="button"
            $active={period === 'yesterday'}
            onClick={() => setPeriod('yesterday')}
            aria-pressed={period === 'yesterday'}>
            Yesterday
          </Tab>
        </Tabs>
        <PerformanceWidgetSelect
          options={scopeOptions.map((o) => ({
            label: o.label,
            value: o.value,
          }))}
          value={scope}
          onChange={(e) => onScopeChange(e.target.value as ScopeKey)}
          selectType="button"
          buttonProps={{
            color: 'tertiary',
            size: 'sm',
          }}
        />
      </HeaderRow>
      <MetricsRow>
        {orgType === 'CHAIN' ? (
          <>
            <PlainCircle label="Opportunity" value={oppty} />
            <PlainCircle label="Sits" value={sit} />
            <PlainCircle label="Sales" value={sales.value} />
            <PlainCircle label="Close" value={close.value} />
          </>
        ) : (
          <>
            <PlainCircle label="Pitches" value={pitches} />
            <PlainCircle label="Hours" value={hours} />
            {orgType === 'STREET' && (
              <PlainCircle label="Accounts" value={accounts} />
            )}
            {orgType === 'BLOCK' && (
              <>
                <PlainCircle label="Sits" value={sits} />
                <PlainCircle label="Leads" value={leads} />
              </>
            )}
            <PlainCircle label="Sales" value={sales.value} />
            <PlainCircle label="Close" value={close.value} />
          </>
        )}
      </MetricsRow>
    </Root>
  );
};

const PlainCircle = ({ label, value }: { label: string; value: number }) => (
  <MetricBlock>
    <PlainDisc aria-hidden>
      <PlainValue>{formatDisplayNumber(value)}</PlainValue>
    </PlainDisc>
    <MetricLabel>{label}</MetricLabel>
  </MetricBlock>
);

const Root = styled.section`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0 0 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${NAVY};
  border-bottom: ${(p) =>
    p.$active ? `3px solid ${NAVY}` : '3px solid transparent'};
  &:focus-visible {
    outline: 2px solid ${NAVY};
    outline-offset: 2px;
  }
`;

const MetricsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
`;

const MetricBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
`;

const PlainDisc = styled.div`
  width: 100%;
  max-width: 72px;
  aspect-ratio: 1;
  height: auto;
  border-radius: 50%;
  background: ${CIRCLE_GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlainValue = styled.span`
  font-size: clamp(10px, 4.5cqi, 18px);
  font-weight: 600;
  color: ${NAVY};
`;

const MetricLabel = styled.span`
  font-size: clamp(8px, 3cqi, 12px);
  font-weight: 500;
  color: ${NAVY};
  text-align: center;
`;

export { DailySnapshot };
